import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  Input,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TourService } from 'ngx-ui-tour-md-menu';
import { tap } from 'rxjs/operators';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { AppService } from '../../../app.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { UtilsService } from '../../../shared/utils.service';
import { CustomDataSource } from '../../../utils/custom-data-source';
import { SitesFilterComponent } from '../../sites-filter/sites-filter.component';
import { ActivatedRoute, Router } from '@angular/router';
import { SitesFilter } from '../../sites-filter/sites-filter.interface';
import { saveAs } from 'file-saver';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-sites-children',
  templateUrl: './sites-children.component.html',
  styleUrls: ['./sites-children.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesChildrenComponent implements OnInit, AfterViewInit {
  // The list of columns that should be displayed.
  displayedColumns: string[] = [
    'select',
    'id',
    'name',
    'location',
    'site_type',
    'site_status',
    'site_risk_assessment',
    'geofence',
    'date_created',
    'actions'
  ];

  // The data source to use for the list of child sites.
  dataSource: ChildSitesDataSource = new ChildSitesDataSource(
    this.app,
    this.api
  );

  // The paginator and sorter.
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  // Get the parent site id as a numeric input.
  parent_site_id: number;

  // Permission flags resolved ONCE in ngOnInit() so the template does not
  // re-evaluate rbac.userCanAccess() on every change detection cycle.
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;
  canCreateMessages: boolean = false;
  canViewTags: boolean = false;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public tour: TourService,
    public utils: UtilsService,
    private route: ActivatedRoute,
    private router: Router,
    public rbac: RBACService
  ) {}

  ngOnInit(): void {
    this.parent_site_id = Number(
      this.route.parent.snapshot.params['parent_id']
    );

    // Resolve the permission flags first so they are always set, even when an early return below fires.
    this.canCreate = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES,
        RBACService.RBAC_A.A_CREATE
      )
    ]);
    this.canView = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES,
        RBACService.RBAC_A.A_VIEW
      )
    ]);
    this.canEdit = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES,
        RBACService.RBAC_A.A_EDIT
      )
    ]);
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_SITES,
        RBACService.RBAC_A.A_DELETE
      )
    ]);
    this.canCreateMessages = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_MESSAGES,
        RBACService.RBAC_A.A_CREATE
      )
    ]);
    this.canViewTags = this.rbac.userCanAccess([
      RBACService.getPermission(
        RBACService.RBAC_F.F_ACCOUNT_TAGS,
        RBACService.RBAC_A.A_VIEW
      )
    ]);

    // The user must be able to view parent sites to view child sites.

    if (!this.canView) {
      this.router.navigate(['/dashboard']);
      return;
    }

    if (!this.parent_site_id) {
      this.router.navigate(['/sites']);
      return;
    }

    this.dataSource.parent_site_id = this.parent_site_id;

    this.dataSource.sites_export_term = this.utils.getLangTerm(
      'sites.plural',
      'Sites'
    );

    this.dataSource.getData();
  }

  ngAfterViewInit(): void {
    if (!this.canView) {
      return;
    }

    this.sort.sortChange.subscribe(
      (): void => {
        this.paginator.pageIndex = 0;
      }
    );

    const _tap = tap(
      (): void => {
        this.dataSource.limit = this.paginator.pageSize;
        this.dataSource.offset = this.paginator.pageIndex;
        this.dataSource.sort_order = this.sort.direction;

        this.dataSource.sort_by = this.sort.active;

        this.dataSource.getData();
      }
    );

    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Filter sites by status.
   * @param status
   */
  onSiteStatusFilter(status: string): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ])
    ) {
      return;
    }

    this.dataSource.status = status;
    this.dataSource.getData(true);
  }

  /**
   * Filter sites by type.
   * @param type
   */
  onSiteTypeFilter(type: string): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ])
    ) {
      return;
    }

    this.dataSource.type = type;
    this.dataSource.getData(true);
  }

  /**
   * Remove a single site.
   * @param id
   */
  onRemove(id: number): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_DELETE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showModal(
      'Remove ' + this.utils.getLangTerm('child-sites.singular', 'Subsite'),
      'Are you sure you want to remove this ' +
        this.utils
          .getLangTerm('child-sites.singular', 'Subsite')
          .toLowerCase() +
        '?',
      (): void => {
        this.api
          .makeRequest('delete', 'v1/sites/' + id)
          .then(
            (): void => {
              this.remove();
            }
          )
          .catch(
            (err: any): void => {
              this.utils.showModal('Error', err.error.message);
            }
          );
      }
    );
  }

  /**
   * Remove selected sites.
   */
  onRemoveSelected(): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_DELETE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showModal(
      'Remove ' + this.utils.getLangTerm('child-sites.plural', 'Subsites'),
      'Are you sure you want to remove the selected ' +
        this.utils.getLangTerm('child-sites.plural', 'Subsites').toLowerCase() +
        '?',
      (): void => {
        this.api
          .makeRequest(
            'delete',
            'v1/sites/' + this.dataSource.selection.selected.join(',')
          )
          .then(
            (): void => {
              this.remove();
            }
          )
          .catch(
            (err: any): void => {
              this.utils.showModal('Error', err.error.message);
            }
          );
      }
    );
  }

  /**
   * Show a success message after site removal.
   */
  private remove(): void {
    this.utils.showToast(
      'The selected ' +
        this.utils.getLangTerm('child-sites.plural', 'Subsites').toLowerCase() +
        ' was successfully deleted.'
    );
    this.dataSource.selection.clear();
    this.dataSource.getData();
  }

  /**
   * Clone a site.
   * @param id
   */
  onCloneSite(id: number): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showModal(
      'Cloning ' + this.utils.getLangTerm('child-sites.singular', 'Subsite'),
      'You are about to clone a ' +
        this.utils
          .getLangTerm('child-sites.singular', 'Subsite')
          .toLowerCase() +
        '. Cloning a ' +
        this.utils
          .getLangTerm('child-sites.singular', 'Subsite')
          .toLowerCase() +
        ' will also clone linked ' +
        this.utils.getLangTerm('contractors.plural', 'Contractors') +
        ', managers, documents, hazards & risks, ' +
        this.utils
          .getLangTerm('hazardous-substances.plural', 'Hazardous Substances')
          .toLowerCase() +
        ', emergency response plans and ' +
        this.utils.getLangTerm('ta-jsa-swms.plural', 'TA/JSA/SWMS') +
        ' records. <br><br>Are you sure you want to continue?',
      (): void => {
        this.api.makeRequest('post', `v1/sites/${id}/clone`).then(
          (response: any): void => {
            this.utils.showToast(response.message);
            this.dataSource.getData();
          }
        );
      }
    );
  }

  /**
   * Compose a new message with a site pre-selected.
   */
  composeNewMessage(): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_MESSAGES,
          RBACService.RBAC_A.A_CREATE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    if (this.dataSource.selection.selected.length == 0) {
      this.utils.showToast(
        'You need to select some ' +
          this.utils
            .getLangTerm('child-sites.plural', 'Subsites')
            .toLowerCase() +
          ' first.'
      );
      return;
    }

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectSiteIds: this.dataSource.selection.selected
        },
        {
          width: '1200px'
        }
      )
      .then(
        (response: any): void => {
          if (response) {
            this.utils.showToast(
              'You can view the message stats in the messages section.'
            );
          }
        }
      );
  }

  getOutputTagsFilter(tag_ids: number[]): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_TAGS,
          RBACService.RBAC_A.A_VIEW
        )
      ])
    ) {
      return;
    }

    this.dataSource.tag_ids = tag_ids;
    this.dataSource.getData(true);
  }

  /**
   * Filter the list of sites.
   */
  onFilter(): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.showComponentDialog(
      SitesFilterComponent,
      {
        site_status: this.dataSource.status,
        site_type: this.dataSource.type,
        date_range: this.dataSource.date_filter_range
      } as SitesFilter,
      {
        width: '1024px'
      },
      (results: SitesFilter): void => {
        if (typeof results != 'undefined') {
          this.dataSource.status =
            typeof results.site_status != 'undefined'
              ? results.site_status
              : this.dataSource.status;
          this.dataSource.type =
            typeof results.site_type != 'undefined'
              ? results.site_type
              : this.dataSource.type;
          this.dataSource.date_filter_range = results.date_range
            ? results.date_range
            : this.dataSource.date_filter_range;
          this.dataSource.getData();
        }
      }
    );
  }

  protected readonly RBACService = RBACService;
}

/**
 * The data source to get a list of sites filtered by a parent site.
 */
export class ChildSitesDataSource extends CustomDataSource {
  // List filtering options.
  sort_by: string = 'name';
  sort_order: string = 'asc';

  status: string = '';
  type: string = '';
  tag_ids: number[] = [];
  date_filter_range: Date[] = [];

  // The parent site id to filter by.
  parent_site_id: number;

  sites_export_term: string = 'Sites';
  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI(
      'sites',
      resetOffset,
      {},
      {
        status: this.status ? this.status : '',
        type: this.type ? this.type : '',
        tag_ids: this.tag_ids.length ? this.tag_ids.join(',') : '',
        date_filter_range:
          this.date_filter_range && this.date_filter_range.length > 1
            ? [
                this.date_filter_range[0].getTime() / 1000,
                this.date_filter_range[1].getTime() / 1000
              ].join(',')
            : '',
        parent_site_id: this.parent_site_id
      }
    );
  }

  /**
   * Export data from the API.
   * @param type
   * @param id
   */
  onExport(type: string = 'pdf', id?: number) {
    // Check if a single site or list of sites should be downloaded.
    let ids: number[] = [];
    if (id) {
      ids.push(id);
    } else {
      ids.push(...this.selection.selected);
    }

    // Make a download request for the list of sites.
    this.api
      .makeDownloadRequest(
        `v1/sites/export/${type}` + (ids.length > 0 ? '/' + ids.join(',') : ''),
        {},
        {
          status: this.status ? this.status : '',
          type: this.type ? this.type : '',
          date_filter_range:
            this.date_filter_range && this.date_filter_range.length > 1
              ? [
                  this.date_filter_range[0].getTime() / 1000,
                  this.date_filter_range[1].getTime() / 1000
                ].join(',')
              : '',
          parent_site_id: this.parent_site_id
        }
      )
      .then((response) => {
        saveAs(
          response,
          `${this.app.account.name} - ${this.sites_export_term}.${type}`
        );
      });
  }
}
